<template>
  <f7-page name="save-to-buy-campaign">
    <f7-navbar :title="$t('saveToBuy.saveTogether')" back-link />
    <AppLayout>
      <template #main>
        <f7-block v-if="loading" class="stb-loading">
          <f7-preloader />
        </f7-block>

        <template v-else-if="campaign">
          <!-- Hero Card with Status & Escrow -->
          <f7-card class="stb-hero" :padding="false">
            <f7-card-content class="stb-hero-content" :padding="false">
              <h1 class="stb-title">{{ campaign.title }}</h1>
              <p v-if="campaign.description" class="stb-desc">{{ campaign.description }}</p>
              <div class="stb-status-row display-flex align-items-center flex-wrap gap-2">
                <f7-badge :style="{ color: statusMeta.color, background: statusBg }" class="font-label-sm font-semibold">{{ statusLabel }}</f7-badge>
              </div>

              <f7-progressbar :progress="progress" class="mt-3" />
              <div class="stb-stats display-flex justify-content-space-around text-align-center mt-3">
                <div class="stb-stat display-flex flex-direction-column">
                  <strong class="font-headline-md text-primary">{{ campaign.currentQuantity }}</strong>
                  <span class="font-label-sm text-on-surface-variant">{{ t("saveToBuy.reserved") }}</span>
                </div>
                <div class="stb-stat display-flex flex-direction-column">
                  <strong class="font-headline-md">{{ campaign.targetQuantity }}</strong>
                  <span class="font-label-sm text-on-surface-variant">{{ t("saveToBuy.needed") }}</span>
                </div>
                <div class="stb-stat display-flex flex-direction-column">
                  <strong class="font-headline-md">{{ remaining }}</strong>
                  <span class="font-label-sm text-on-surface-variant">{{ t("saveToBuy.remaining") }}</span>
                </div>
              </div>

              <p class="stb-price display-flex align-items-baseline gap-1 mt-3">
                {{ t("saveToBuy.currentGroupPrice") }}
                <span class="font-headline-md text-primary">{{ groupPrice }}</span>
              </p>
              <p class="stb-unit-price font-label-sm text-on-surface-variant mt-1">
                {{ t("saveToBuy.individualPrice") }} {{ fmtCfa(campaign.individualPrice) }}
              </p>

              <!-- Next Tier Teaser -->
              <div v-if="nextTier" class="next-tier-teaser mt-3 p-3 rounded-lg bg-secondary-container/20 display-flex align-items-center justify-content-between">
                <div class="display-flex align-items-center gap-2 text-on-secondary-container">
                  <f7-icon material="trending_down" :size="18" class="text-secondary" />
                  <span class="font-label-md text-label-md">
                    {{ t('saveToBuy.nextTierTeaser', { price: fmtCfa(nextTier.unitPrice), n: nextTier.minimumQuantity }) }}
                  </span>
                </div>
                <span class="font-label-sm text-label-sm text-secondary font-bold">{{ unlockedPct(nextTier) }}</span>
              </div>
            </f7-card-content>
          </f7-card>

          <!-- Tier Matrix (Group Pricing Scale) -->
          <f7-card class="tier-matrix-card" :padding="false">
            <f7-card-content class="tier-matrix-content" :padding="false">
              <div class="display-flex align-items-center justify-content-space-between mb-3">
                <div class="display-flex align-items-center gap-2">
                  <f7-icon material="stacked_bar_chart" :size="20" class="text-primary" />
                  <h2 class="font-headline-sm text-on-surface">{{ t('saveToBuy.tierMatrixTitle') }}</h2>
                </div>
                <f7-badge class="font-label-sm font-medium bg-primary/10 text-primary">{{ t('saveToBuy.wholesaleContract') }}</f7-badge>
              </div>

              <div class="tier-matrix grid grid-cols-3 gap-2 text-center">
                <template v-for="(tier, i) in tiers" :key="tier.minimumQuantity">
                  <f7-card class="tier-card" :padding="false"
                    :class="{
                      'bg-primary-fixed/25 shadow-sm': unlocked(tier),
                      'bg-surface-container-low opacity-70': !unlocked(tier) && i === 0,
                      'bg-surface-container-low opacity-80': !unlocked(tier) && i > 0,
                    }"
                  >
                    <f7-card-content class="tier-card-content" :padding="false">
                      <span class="font-label-sm text-label-sm text-on-surface-variant">{{ t('saveToBuy.tierPalier', { n: i + 1 }) }}</span>
                      <span class="font-label-md text-label-md font-bold text-on-surface">{{ tier.minimumQuantity }}{{ tier.maximumQuantity ? `-${tier.maximumQuantity}` : '+' }}</span>
                      <span class="font-label-sm text-label-sm text-outline">{{ t('saveToBuy.units', { count: tier.minimumQuantity }) }}</span>
                      <div class="w-full h-0.5 bg-outline-variant my-1"></div>
                      <span class="font-label-md text-label-md font-semibold" :class="{ 'text-primary': unlocked(tier), 'text-on-surface': !unlocked(tier) }">{{ fmtCfa(tier.unitPrice) }}</span>
                      <span class="tier-savings font-label-sm" :class="{ 'text-secondary font-bold': unlocked(tier), 'text-outline': !unlocked(tier) }">
                        {{ tierSavings(tier) }}
                      </span>
                      <f7-badge v-if="unlocked(tier)" class="tier-active-badge font-label-sm bg-primary text-on-primary rounded-bl">{{ t('saveToBuy.tierActive') }}</f7-badge>
                    </f7-card-content>
                  </f7-card>
                </template>
              </div>

              <div class="tier-footnote mt-3 p-2 rounded-lg bg-surface-container/50 display-flex align-items-center gap-2 text-on-surface-variant">
                <f7-icon material="verified_user" :size="16" class="text-primary" />
                <p class="font-body-sm leading-tight tier-footnote-text">{{ t('saveToBuy.tierFootnote') }}</p>
              </div>
            </f7-card-content>
          </f7-card>

          <!-- Trust Strip -->
          <f7-card class="trust-strip" :padding="false">
            <f7-card-content class="trust-strip-content" :padding="false">
              <f7-icon material="lock_clock" :size="24" class="text-primary flex-shrink-0 mt-0.5" />
              <div class="flex flex-col gap-1">
                <h4 class="font-label-md text-label-md text-on-surface font-semibold">{{ t('saveToBuy.trustTitle') }}</h4>
                <p class="font-body-sm text-body-sm text-on-surface-variant">{{ t('saveToBuy.trustText') }}</p>
              </div>
            </f7-card-content>
          </f7-card>

          <!-- Participant block -->
          <f7-card v-if="participant" class="stb-participant" :padding="false">
            <f7-card-content class="stb-participant-content" :padding="false">
              <f7-block-title class="stb-participant-title">
                {{ $t("saveToBuy.myCommitment") }}
              </f7-block-title>
              <div class="stb-participant-rows">
                <div class="stb-row display-flex justify-content-space-between">
                  <span class="stb-row-label">{{ $t("saveToBuy.quantity") }}</span>
                  <strong>{{ participant.quantity }}</strong>
                </div>
                <div class="stb-row display-flex justify-content-space-between">
                  <span class="stb-row-label">{{ $t("saveToBuy.committedAmount") }}</span>
                  <strong>{{ fmtCfa(participant.committedAmount) }}</strong>
                </div>
                <div class="stb-row display-flex justify-content-space-between" v-if="Number(participant.escrowedAmount) > 0">
                  <span class="stb-row-label">{{ $t("saveToBuy.escrowedAmount") }}</span>
                  <strong>{{ fmtCfa(participant.escrowedAmount) }}</strong>
                </div>
                <div class="stb-row display-flex justify-content-space-between" v-if="Number(participant.remainingToCommit) > 0">
                  <span class="stb-row-label">{{ $t("saveToBuy.remainingToCommit") }}</span>
                  <strong>{{ fmtCfa(participant.remainingToCommit) }}</strong>
                </div>
              </div>
              <p class="stb-participant-status display-flex align-items-center gap-2 mt-3">
                <f7-badge :style="{ color: participantMeta.color, background: participantBg }" class="font-label-sm font-semibold">{{ participantLabel }}</f7-badge>
                <span class="font-label-sm text-on-surface-variant">{{ paymentModeLabel }}</span>
              </p>

              <div class="stb-space-y display-flex flex-direction-column gap-2 mt-3">
                <f7-button v-if="participant.status === 'JOINED' && !failed" fill large @click="doCommit">{{ $t("saveToBuy.commitNow") }}</f7-button>
                <f7-button v-if="participant.status === 'JOINED' && participant.paymentMode === 'ESCROW' && !failed" round raised fill large @click="doEscrowAndPay">{{ $t("saveToBuy.escrowNow", { amount: escrowAmountLabel }) }}</f7-button>
                <f7-button v-if="participant.status === 'JOINED' && participant.paymentMode === 'INSTALLMENT' && !failed"round raised  fill large @click="setupInstallments">{{ $t("saveToBuy.setupInstallments") }}</f7-button>
                <f7-button v-if="participant.status === 'COMMITTED' && !failed && participant.paymentMode === 'COMMITMENT_ONLY'"round raised  outline large disabled>{{ $t("saveToBuy.commitmentOnlyHint") }}</f7-button>
              </div>
            </f7-card-content>
          </f7-card>

          <!-- Installment schedule -->
          <f7-card v-if="participant && participant.paymentMode === 'INSTALLMENT' && contributions.length" :padding="false">
            <f7-card-content class="stb-installments-content" :padding="false">
              <f7-block-title>{{ $t("saveToBuy.installments") }}</f7-block-title>
              <f7-list inset strong>
                <f7-list-item v-for="contribution in contributions" :key="contribution.id" :title="$t('saveToBuy.installmentN', { n: contribution.installmentNumber })" :subtitle="dueLabel(contribution)">
                  <template #after>
                    <span class="stb-contribution-after display-flex align-items-center gap-2">
                      <span :class="contribution.isPaid ? 'stb-paid' : 'stb-due'">{{ fmtCfa(contribution.expectedAmount) }}</span>
                      <f7-badge :color="contribution.isPaid ? 'green' : contribution.isOverdue ? 'red' : 'orange'">{{ $t(contributionMeta(contribution).labelKey) }}</f7-badge>
                      <f7-button v-if="!contribution.isPaid" small round raised  fill @click="payContribution(contribution)">{{ $t("common.pay") }}</f7-button>
                    </span>
                  </template>
                </f7-list-item>
              </f7-list>
              <f7-button v-if="pendingPay" small round raised  outline class="stb-btn" @click="confirmContributionPaid">{{ $t("saveToBuy.confirmPayment") }}</f7-button>
            </f7-card-content>
          </f7-card>

          <!-- Join (not yet a participant) -->
          <f7-card v-if="!participant && campaign.isAcceptingParticipants && !failed" :padding="false">
            <f7-card-content class="stb-join-content" :padding="false">
              <f7-button fill large round raised  @click="openJoin">{{ $t("saveToBuy.joinAndSave") }}</f7-button>
              <f7-button outline large round raised  class="stb-btn" @click="invite">
                <f7-icon f7="paperplane" :size="16" />
                {{ $t("saveToBuy.inviteFriends") }}
              </f7-button>
            </f7-card-content>
          </f7-card>

          <!-- Creator lifecycle -->
          <f7-card v-if="isCreator" class="stb-lifecycle" :padding="false">
            <f7-card-content class="stb-lifecycle-content" :padding="false">
              <f7-block-title>{{ $t("saveToBuy.manageCampaign") }}</f7-block-title>
              <div class="stb-space-y display-flex flex-direction-column gap-2">
                <f7-button v-if="status === 'ACTIVE'" round raised  fill large @click="doTargetReached">{{ $t("saveToBuy.markTargetReached") }}</f7-button>
                <f7-button v-if="status === 'TARGET_REACHED'" round raised  fill large @click="doAllocate">{{ $t("saveToBuy.startAllocation") }}</f7-button>
                <f7-button v-if="status === 'ALLOCATING'" round raised  fill large @click="doSuccess">{{ $t("saveToBuy.markSuccessful") }}</f7-button>
                <f7-button v-if="['ACTIVE', 'TARGET_REACHED', 'ALLOCATING'].includes(status)" outline large color="red" @click="doCancel">{{ $t("saveToBuy.cancelCampaign") }}</f7-button>
              </div>
            </f7-card-content>
          </f7-card>

          <!-- Failure outcome -->
          <f7-card v-if="failed" class="stb-outcome" :padding="false">
            <f7-card-content class="stb-outcome-content" :padding="false">
              <p class="stb-outcome-title font-label-lg font-semibold text-error">{{ $t("saveToBuy.campaignEnded", { current: campaign.currentQuantity, target: campaign.targetQuantity }) }}</p>
              <p class="stb-outcome-hint font-body-sm text-on-surface-variant">{{ $t("saveToBuy.chooseOutcome") }}</p>
              <f7-button round raised  fill large class="stb-outcome-btn" :disabled="!participant" @click="doBuyAlone">{{ $t("saveToBuy.outcomeBuyAlone", { price: fmtCfa(campaign.individualPrice) }) }}</f7-button>
              <f7-button round raised  outline large class="stb-outcome-btn" :disabled="!participant" @click="doRefund">{{ $t("saveToBuy.outcomeRefund") }}</f7-button>
            </f7-card-content>
          </f7-card>
        </template>

        <f7-block v-else>
          <p class="stb-empty">{{ $t("saveToBuy.campaignNotFound") }}</p>
        </f7-block>
      </template>
    </AppLayout>

    <AdaptiveSheet v-model:opened="joinOpen" class="stb-join-sheet">
      <f7-page-content>
        <f7-block-title class="stb-sheet-title">{{ $t("saveToBuy.joinAndSave") }}</f7-block-title>
        <f7-list inset strong form>
          <f7-list-input v-model:value="joinForm.quantity" type="number" :label="$t('saveToBuy.quantity')" />
          <f7-list-input :label="$t('saveToBuy.paymentMode')" v-model="joinForm.paymentMode" type="select">
                <option v-for="m in PAYMENT_MODES" :key="m.code" :value="m.code">{{ $t(m.labelKey) }}</option>
          </f7-list-input>
        </f7-list>
        <f7-block>
          <f7-button round raised  large fill :disabled="!canJoin" @click="join">{{ $t("saveToBuy.confirmJoin") }}</f7-button>
        </f7-block>
      </f7-page-content>
    </AdaptiveSheet>
  </f7-page>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import AppLayout from "../components/common/AppLayout.vue";
import AdaptiveSheet from "../components/common/AdaptiveSheet.vue";
import { f7 } from "framework7-vue";
import { fmtCfa } from "../constants/listing-options.js";
import { usePaymentTrigger } from "../js/composables/usePaymentTrigger.js";
import {
  newTransactionReference,
  getPaymentStatus,
} from "../js/api/payments.js";
import {
  useSaveToBuyStore,
  CAMPAIGN_STATUS_META,
  PARTICIPANT_STATUS_META,
  CONTRIBUTION_STATUS_META,
  PAYMENT_MODES,
  INSTALLMENTS_DEFAULT_MONTHS,
  suggestTiers,
  tierUnitPrice,
} from "../js/pinia-stores/saveToBuy.js";
import { useKeycloakStore } from "../js/pinia-stores/keycloak.js";
import { useGroupsStore } from "../js/pinia-stores/group.js";
import i18n from "../js/i18n.js";

const props = defineProps({
  groupId: { type: String, required: true },
  campaignId: { type: String, required: true },
});

const { t } = i18n.global;
const saveToBuyStore = useSaveToBuyStore();
const keycloakStore = useKeycloakStore();
const groupsStore = useGroupsStore();
const paymentTrigger = usePaymentTrigger({ f7router: f7.view.main.router });

const loading = ref(true);
const joinOpen = ref(false);
const joinForm = reactive({ quantity: 1, paymentMode: "COMMITMENT_ONLY" });
const pendingPay = ref(null);

const me = computed(() => keycloakStore.subject || "");
const campaign = computed(() => saveToBuyStore.activeCampaign);
const participant = computed(() => saveToBuyStore.activeParticipant);
const contributions = computed(() => saveToBuyStore.contributions);

const status = computed(() => campaign.value?.status);
const isCreator = computed(
  () =>
    campaign.value &&
    String(campaign.value.creatorUserId) === String(me.value),
);
const failed = computed(() =>
  ["EXPIRED", "CANCELLED"].includes(campaign.value?.status),
);

const statusMeta = computed(
  () => CAMPAIGN_STATUS_META[status.value] || CAMPAIGN_STATUS_META.DRAFT,
);
const statusLabel = computed(() => t(statusMeta.value.labelKey));
const statusBg = computed(() => colorMix(statusMeta.value.color, 12));

const participantMeta = computed(
  () =>
    PARTICIPANT_STATUS_META[participant.value?.status] ||
    PARTICIPANT_STATUS_META.JOINED,
);
const participantLabel = computed(() => t(participantMeta.value.labelKey));
const participantBg = computed(() => colorMix(participantMeta.value.color, 12));

const paymentModeLabel = computed(() => {
  const mode = (PAYMENT_MODES || []).find(
    (m) => m.code === participant.value?.paymentMode,
  );
  return mode ? t(mode.labelKey) : "";
});

const tiers = computed(() => {
  if (campaign.value?.tiers?.length) return campaign.value.tiers;
  if (campaign.value?.individualPrice) return suggestTiers(campaign.value.individualPrice);
  return [];
});
const progress = computed(() =>
  Math.min(
    (Number(campaign.value?.currentQuantity) /
      Number(campaign.value?.targetQuantity || 1)) *
      100,
    100,
  ),
);
const remaining = computed(() => {
  if (Number(campaign.value?.remainingQuantity) > 0) {
    return campaign.value.remainingQuantity;
  }
  const d = new Date(campaign.value?.endsAt) - Date.now();
  if (d <= 0) return t("saveToBuy.ended");
  return `${Math.floor(d / 86400000)}d ${Math.floor((d % 86400000) / 3600000)}h`;
});
const groupPrice = computed(() => {
  if (campaign.value?.currentUnitPrice != null) {
    return fmtCfa(campaign.value.currentUnitPrice);
  }
  return fmtCfa(
    tierUnitPrice(tiers.value, campaign.value?.currentQuantity),
  );
});

// New: Next tier for teaser
const nextTier = computed(() => {
  const sorted = [...tiers.value].sort((a, b) => Number(a.minimumQuantity) - Number(b.minimumQuantity));
  return sorted.find(t => !unlocked(t)) || sorted[sorted.length - 1];
});

const unlockedPct = (tier) => {
  if (!campaign.value?.individualPrice) return '';
  const pct = Math.round((1 - Number(tier.unitPrice) / Number(campaign.value.individualPrice)) * 100);
  return pct > 0 ? `-${pct}%` : '';
};

const tierSavings = (tier) => {
  if (!campaign.value?.individualPrice) return '';
  const saving = Number(campaign.value.individualPrice) - Number(tier.unitPrice);
  return saving > 0 ? `${t('saveToBuy.savings', { amount: fmtCfa(saving) })}` : '';
};

const escrowAmountLabel = computed(() =>
  fmtCfa(participant.value?.committedAmount),
);
const canJoin = computed(() => Number(joinForm.quantity) > 0);

function unlocked(tier) {
  return Number(campaign.value?.currentQuantity) >= Number(tier.minimumQuantity);
}
function contributionMeta(contribution) {
  return (
    CONTRIBUTION_STATUS_META[contribution.status] ||
    CONTRIBUTION_STATUS_META.PENDING
  );
}
function dueLabel(contribution) {
  if (contribution.dueAt)
    return `${t("saveToBuy.dueAt")} ${new Date(contribution.dueAt).toLocaleDateString()}`;
  return "";
}
function colorMix(hex, alpha) {
  const h = hex.replace("#", "");
  const bigint = parseInt(h, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha / 100})`;
}

onMounted(async () => {
  await Promise.all([
    groupsStore.fetchGroupDetail(props.groupId).catch(() => {}),
  ]);
  try {
    await saveToBuyStore.getCampaign(props.campaignId);
  } catch (error) {
    f7.dialog.alert(
      error?.response?.data?.message || t("saveToBuy.errorGeneric"),
    );
  }
  try {
    await saveToBuyStore.getMyParticipant(props.campaignId, me.value);
  } catch {
    saveToBuyStore.activeParticipant = null;
  }
  if (saveToBuyStore.activeParticipant?.paymentMode === "INSTALLMENT") {
    await saveToBuyStore
      .getContributions(saveToBuyStore.activeParticipant.id)
      .catch(() => {});
  }
  loading.value = false;
});

function openJoin() {
  joinForm.quantity = 1;
  joinForm.paymentMode = "COMMITMENT_ONLY";
  joinOpen.value = true;
}

async function join() {
  if (!canJoin.value) return;
  f7.preloader.show();
  try {
    const quantity = Number(joinForm.quantity);
    const unit = Number(campaign.value?.individualPrice) || 0;
    await saveToBuyStore.joinCampaign(props.campaignId, {
      userId: me.value,
      quantity,
      committedAmount: unit * quantity,
      paymentMode: joinForm.paymentMode,
    });
    joinOpen.value = false;
    f7.toast.show({ text: t("saveToBuy.joinedToast"), closeTimeout: 2500 });
    await saveToBuyStore.getCampaign(props.campaignId);
    if (joinForm.paymentMode === "ESCROW") {
      await doEscrowAndPay();
    } else if (joinForm.paymentMode === "INSTALLMENT") {
      await setupInstallments();
    }
  } catch (error) {
    f7.dialog.alert(
      error?.response?.data?.message || t("saveToBuy.errorGeneric"),
    );
  } finally {
    f7.preloader.hide();
  }
}

async function doCommit() {
  f7.preloader.show();
  try {
    await saveToBuyStore.commitFunds(participant.value.id);
    f7.toast.show({ text: t("saveToBuy.commitSaved"), closeTimeout: 2000 });
  } catch (error) {
    f7.dialog.alert(
      error?.response?.data?.message || t("saveToBuy.errorGeneric"),
    );
  } finally {
    f7.preloader.hide();
  }
}

async function doEscrowAndPay() {
  const id = participant.value.id;
  const amount = Number(participant.value.committedAmount) || 0;
  f7.preloader.show();
  try {
    if (participant.value.status === "JOINED") {
      await saveToBuyStore.escrowFunds(id);
    }
    f7.preloader.hide();
    paymentTrigger.triggerSaveToBuyContribution({
      campaignId: props.campaignId,
      groupId: props.groupId,
      amount,
      campaignName: campaign.value?.title || "",
      groupName: "",
    });
  } catch (error) {
    f7.preloader.hide();
    f7.dialog.alert(
      error?.response?.data?.message || t("saveToBuy.errorGeneric"),
    );
  }
}

async function setupInstallments() {
  const id = participant.value.id;
  const amount = Number(participant.value.committedAmount) || 0;
  f7.preloader.show();
  try {
    const created = await saveToBuyStore.scheduleInstallments(
      id,
      amount,
      INSTALLMENTS_DEFAULT_MONTHS,
    );
    await saveToBuyStore.getContributions(id);
    f7.preloader.hide();
    const next = created[0];
    if (next) await payContribution(next);
  } catch (error) {
    f7.preloader.hide();
    f7.dialog.alert(
      error?.response?.data?.message || t("saveToBuy.errorGeneric"),
    );
  }
}

async function payContribution(contribution) {
  const amount = Number(contribution.expectedAmount) || Number(contribution.remainingAmount) || 0;
  const reference = newTransactionReference();
  pendingPay.value = { contributionId: contribution.id, reference, amount };
  paymentTrigger.triggerSaveToBuyContribution({
    campaignId: props.campaignId,
    groupId: props.groupId,
    amount,
    campaignName: campaign.value?.title || "",
    groupName: "",
    tag: reference,
  });
}

async function confirmContributionPaid() {
  if (!pendingPay.value) return;
  const { contributionId, reference, amount } = pendingPay.value;
  f7.preloader.show();
  try {
    const status = await getPaymentStatus(reference);
    if (status?.isSuccessful || status?.status === "SUCCESS") {
      await saveToBuyStore.markContributionPaid(contributionId, {
        paymentReference: reference,
        amount,
      });
      pendingPay.value = null;
      await saveToBuyStore.getContributions(participant.value.id);
      f7.toast.show({ text: t("saveToBuy.contributionPaid"), closeTimeout: 2500 });
    } else {
      f7.dialog.alert(t("saveToBuy.paymentNotConfirmed"));
    }
  } catch (error) {
    f7.dialog.alert(
      error?.response?.data?.message || t("saveToBuy.paymentNotConfirmed"),
    );
  } finally {
    f7.preloader.hide();
  }
}

async function invite() {
  const url =
    window.location.origin +
    `/groups/${props.groupId}/save-to-buy/campaigns/${props.campaignId}`;
  const text = `${campaign.value?.title || ""} — ${t("saveToBuy.shareHint")}`;
  if (navigator.share) {
    try {
      await navigator.share({ title: campaign.value?.title || "", text, url });
    } catch {
      // share dismissed — keep silent
    }
  } else {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      window.prompt(t("saveToBuy.shareManual"), url);
      return;
    }
    f7.toast.show({ text: t("saveToBuy.linkCopied"), closeTimeout: 1800 });
  }
}

// ── Creator lifecycle ─────────────────────────────────────────────

async function activateCampaign() {
  await runCampaignAction(() =>
    saveToBuyStore.activateCampaign(props.campaignId),
  );
}
async function doTargetReached() {
  await runCampaignAction(() =>
    saveToBuyStore.markTargetReached(props.campaignId),
  );
}
async function doAllocate() {
  await runCampaignAction(() => saveToBuyStore.startAllocation(props.campaignId));
}
async function doSuccess() {
  await runCampaignAction(() =>
    saveToBuyStore.markCampaignSuccessful(props.campaignId),
  );
}
async function doCancel() {
  await runCampaignAction(() => saveToBuyStore.cancelCampaign(props.campaignId));
}

async function runCampaignAction(fn) {
  f7.preloader.show();
  try {
    await fn();
    f7.toast.show({ text: t("saveToBuy.campaignUpdated"), closeTimeout: 2000 });
  } catch (error) {
    f7.dialog.alert(
      error?.response?.data?.message || t("saveToBuy.errorGeneric"),
    );
  } finally {
    f7.preloader.hide();
  }
}

// ── Failure outcomes ──────────────────────────────────────────────

async function doBuyAlone() {
  await runParticipantOutcome(() =>
    saveToBuyStore.buyAlone(participant.value.id),
  );
}
async function doRefund() {
  await runParticipantOutcome(() =>
    saveToBuyStore.refundParticipant(participant.value.id),
  );
}
async function runParticipantOutcome(fn) {
  f7.preloader.show();
  try {
    await fn();
    await saveToBuyStore.getMyParticipant(props.campaignId, me.value);
    f7.toast.show({ text: t("saveToBuy.choiceSaved"), closeTimeout: 2000 });
  } catch (error) {
    f7.dialog.alert(
      error?.response?.data?.message || t("saveToBuy.errorGeneric"),
    );
  } finally {
    f7.preloader.hide();
  }
}
</script>

<style scoped>
.stb-loading {
  text-align: center;
  padding: 24px 0;
}
.stb-hero {
  margin: 0;
  box-shadow: none;
  border-radius: 16px;
}
.stb-hero-content {
  position: relative;
  padding: 16px;
}
.stb-title {
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 4px;
}
.stb-desc {
  font-size: 13px;
  line-height: 1.5;
  color: var(--f7-on-surface-variant, #8e8e93);
  margin: 0 0 12px;
}
.stb-status-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}
.stb-stats {
  display: flex;
  justify-content: space-around;
  text-align: center;
  margin: 16px 0 12px;
}
.stb-stat {
  display: flex;
  flex-direction: column;
}
.stb-stat strong {
  font-size: 20px;
  font-weight: 700;
  color: var(--f7-text-color, #161d18);
}
.stb-stat span {
  font-size: 11px;
  color: var(--f7-on-surface-variant, #8e8e93);
  margin-top: 2px;
}
.stb-price {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin: 0;
}
.stb-price span {
  font-size: 22px;
  font-weight: 700;
  color: var(--f7-theme-color);
}
.stb-unit-price {
  font-size: 12.5px;
  color: var(--f7-on-surface-variant, #8e8e93);
  margin: 4px 0 0;
}
.next-tier-teaser {
  border-radius: 12px;
}
.tier-matrix-card {
  margin: 0;
  box-shadow: none;
  border-radius: 16px;
}
.tier-matrix-content {
  position: relative;
  padding: 16px;
}
.tier-matrix {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}
.tier-card {
  margin: 0;
  box-shadow: none;
  border-radius: 12px;
  transition: all 0.2s ease;
}
.tier-card-content {
  position: relative;
  padding: 12px 8px;
}
.tier-savings {
  font-size: 10px;
}
.tier-active-badge {
  position: absolute;
  top: 0;
  right: 0;
  min-width: 0;
  padding: 2px 6px;
  border-radius: 0 0 0 9999px;
}
.tier-footnote {
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  padding: 8px 12px;
}
.tier-footnote-text {
  font-size: 12px;
}
.trust-strip {
  margin: 16px 0 0;
  box-shadow: none;
  border-radius: 12px;
  background: var(--f7-list-bg-color, #f5f5f5);
  border: 1px solid var(--f7-list-item-border-color, var(--f7-list-item-border-color, #e0e0e0));
}
.trust-strip-content {
  position: relative;
  padding: 16px;
}
.stb-tier-price {
  font-size: 13px;
  font-weight: 600;
  margin-right: 8px;
  color: var(--f7-theme-color);
}
.stb-btn {
  margin-top: 8px;
}
.stb-participant {
  margin: 0;
  box-shadow: none;
  border-radius: 16px;
}
.stb-participant-content {
  position: relative;
  padding: 16px;
}
.stb-participant-title {
  margin-top: 0;
}
.stb-participant-rows {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 10px;
}
.stb-row {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
}
.stb-row-label {
  color: var(--f7-on-surface-variant, #8e8e93);
}
.stb-participant-status {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 12px;
}
.stb-space-y {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.stb-installments-content {
  position: relative;
  padding: 16px;
}
.stb-contribution-after {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}
.stb-due {
  font-weight: 700;
  color: var(--f7-text-color, #333);
}
.stb-paid {
  font-weight: 700;
  color: var(--f7-theme-color);
}
.stb-join-content,
.stb-lifecycle-content,
.stb-outcome-content {
  position: relative;
  padding: 16px;
}
.stb-outcome {
  margin: 0;
  box-shadow: none;
  border-radius: 12px;
  background: color-mix(in srgb, #c0392b 6%, transparent);
  border: 1px solid color-mix(in srgb, #c0392b 22%, transparent);
}
.stb-outcome-title {
  font-size: 14px;
  font-weight: 700;
  color: #c0392b;
  margin: 0 0 4px;
}
.stb-outcome-hint {
  font-size: 12.5px;
  color: var(--f7-on-surface-variant, #8e8e93);
  margin: 0 0 12px;
}
.stb-outcome-btn {
  margin-top: 8px;
}
.stb-empty {
  font-size: 13px;
  color: var(--f7-on-surface-variant, #8e8e93);
  padding: 24px 16px;
}
.stb-sheet-title {
  text-align: center;
}
</style>